<template>
  <div>
    <!-- Backdrop with blur effect -->
    <div
      v-if="isVisible"
      class="sidebar-backdrop"
      @click="closeDrawer"
    />

    <!-- Enhanced Sidebar Drawer -->
    <div
      class="sidebar-drawer"
      :class="{ 'sidebar-open': isVisible }"
    >
      <!-- Modern Header -->
      <div class="sidebar-header d-flex align-items-center justify-content-between">
        <h5 class="mb-0 d-flex align-items-center">
          <div class="header-icon-wrapper">
            <feather-icon
              icon="PackageIcon"
              size="18"
            />
          </div>
          <span v-if="order">Colis #{{ order.id }}</span>
          <span v-else>Aperçu rapide</span>
        </h5>
        <b-button
          variant="light-secondary"
          class="btn-icon rounded-circle"
          size="sm"
          @click="closeDrawer"
        >
          <feather-icon
            icon="XIcon"
            size="16"
          />
        </b-button>
      </div>

      <!-- Content -->
      <div class="sidebar-content">
        <!-- Loading State -->
        <div
          v-if="isLoading"
          class="text-center p-5"
        >
          <div class="loading-pulse mb-2" />
          <p class="text-muted">
            Chargement des détails...
          </p>
        </div>

        <!-- Error State -->
        <div
          v-else-if="error"
          class="text-center p-4 error-container"
        >
          <div class="error-icon-wrapper mb-3">
            <feather-icon
              icon="AlertCircleIcon"
              size="32"
            />
          </div>
          <p class="text-danger mb-3">
            {{ error }}
          </p>
          <b-button
            variant="outline-primary"
            size="sm"
            class="btn-retry"
            @click="fetchOrderDetails"
          >
            <feather-icon
              icon="RefreshCwIcon"
              class="mr-1"
              size="14"
            />
            Réessayer
          </b-button>
        </div>

        <!-- Order Details -->
        <div v-else-if="order">
          <!-- Enhanced Customer Info Card -->
          <b-card class="modern-card customer-card enhanced-card mb-3">
            <template #header>
              <div class="card-header-content">
                <div class="header-icon-wrapper-custom">
                  <feather-icon
                    icon="PackageIcon"
                    class="text-white"
                    size="16"
                  />
                </div>
                <h6 class="mb-0 ml-2">
                  Données destinataire & expédition
                  <b-tooltip
                    target="quickview-client-info"
                    placement="top"
                  >
                    Informations détaillées sur le client, le destinataire et l'adresse de livraison.
                  </b-tooltip>
                </h6>
              </div>
            </template>
            <div class="p-0">
              <!-- First Row: Name and Phone -->
              <div class="row mb-2">
                <div class="col-6">
                  <div class="info-item-premium">
                    <div class="info-icon-premium bg-gradient-indigo">
                      <feather-icon
                        icon="UserIcon"
                        size="16"
                        class="text-white"
                      />
                    </div>
                    <div class="info-content-premium">
                      <small
                        id="quickview-client-info"
                        class="text-uppercase font-weight-bold text-muted"
                      >Destinataire</small>
                      <h6 class="mb-0 font-weight-bold text-indigo">
                        {{ order.name }}
                      </h6>
                    </div>
                  </div>
                </div>
                <div class="col-6">
                  <div class="info-item-premium">
                    <div class="info-icon-premium bg-gradient-purple">
                      <feather-icon
                        icon="PhoneIcon"
                        size="16"
                        class="text-white"
                      />
                    </div>
                    <div class="info-content-premium">
                      <small class="text-uppercase font-weight-bold text-muted">Téléphone principal</small>
                      <h6 class="mb-0 font-weight-bold text-purple">
                        <span v-if="order.phone1">{{ order.phone1 }}</span>
                        <span v-if="order.phone2">
                          <br>
                          {{ order.phone2 }}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Second Row: Address and Price -->
              <div class="row mb-2">
                <div class="col-6">
                  <div class="info-item-premium">
                    <div class="info-icon-premium bg-gradient-info">
                      <feather-icon
                        icon="MapPinIcon"
                        size="16"
                        class="text-white"
                      />
                    </div>
                    <div class="info-content-premium">
                      <small class="text-uppercase font-weight-bold text-muted">Adresse de livraison</small>
                      <h6
                        class="mb-0 font-weight-bold text-info text-truncate"
                        :title="order.address"
                      >
                        {{ order.address }}
                      </h6>
                    </div>
                  </div>
                </div>
                <div class="col-6">
                  <div class="info-item-premium">
                    <div class="info-icon-premium bg-gradient-success">
                      <feather-icon
                        icon="DollarSignIcon"
                        size="16"
                        class="text-white"
                      />
                    </div>
                    <div class="info-content-premium">
                      <small class="text-uppercase font-weight-bold text-muted">Mode de paiement du destinataire</small>
                      <h6 class="mb-0 font-weight-bold text-success">
                        {{ getPaymentMethod(order.payment_method) }}
                      </h6>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Third Row: Governorate -->
              <div class="row mb-2">
                <div class="col-12">
                  <div class="info-item-premium">
                    <div class="info-icon-premium bg-gradient-orange">
                      <feather-icon
                        icon="MapIcon"
                        size="16"
                        class="text-white"
                      />
                    </div>
                    <div class="info-content-premium">
                      <small class="text-uppercase font-weight-bold text-muted">Gouvernorat</small>
                      <h6 class="mb-0 font-weight-bold text-orange">
                        {{ order.governorate && order.governorate.name ? order.governorate.name : '-' }}
                      </h6>
                      <template v-if="order.delegation && order.delegation.name">
                        <div
                          class="text-muted"
                          style="font-size:0.95em;"
                        >
                          <feather-icon
                            icon="MapPinIcon"
                            size="13"
                            class="mr-1"
                          />
                          Délégation: {{ order.delegation.name }}
                        </div>
                      </template>
                      <template v-if="order.locality && order.locality.name">
                        <div
                          class="text-muted"
                          style="font-size:0.95em;"
                        >
                          <feather-icon
                            icon="Navigation2Icon"
                            size="13"
                            class="mr-1"
                          />
                          Localité: {{ order.locality.name }}<span v-if="order.locality.zip_code"> ({{ order.locality.zip_code }})</span>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Pickup info (if set) -->
              <div
                v-if="order.pickup_governorate || order.pickup_address"
                class="row mb-2"
              >
                <div class="col-6">
                  <div class="info-item-premium">
                    <div class="info-icon-premium bg-gradient-warning">
                      <feather-icon
                        icon="PackageIcon"
                        size="16"
                        class="text-white"
                      />
                    </div>
                    <div class="info-content-premium">
                      <small class="text-uppercase font-weight-bold text-muted">Gouv. retrait</small>
                      <h6 class="mb-0 font-weight-bold text-warning">
                        {{ order.pickup_governorate && order.pickup_governorate.name ? order.pickup_governorate.name : '-' }}
                      </h6>
                    </div>
                  </div>
                </div>
                <div class="col-6">
                  <div class="info-item-premium">
                    <div class="info-icon-premium bg-gradient-warning">
                      <feather-icon
                        icon="Navigation2Icon"
                        size="16"
                        class="text-white"
                      />
                    </div>
                    <div class="info-content-premium">
                      <small class="text-uppercase font-weight-bold text-muted">Adresse retrait</small>
                      <h6
                        class="mb-0 font-weight-bold text-warning text-truncate"
                        :title="order.pickup_address"
                      >
                        {{ order.pickup_address || '-' }}
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-card>

          <!-- Metadata: Central Hub Reception and Creation Date -->
          <b-card class="modern-card mb-3">
            <template #header>
              <div class="card-header-content">
                <feather-icon
                  icon="InfoIcon"
                  class="text-primary"
                  size="16"
                />
                <h6 class="mb-0 ml-2">
                  Informations complémentaires
                  <b-tooltip
                    target="quickview-meta-info"
                    placement="top"
                  >
                    Métadonnées et informations additionnelles sur le colis.
                  </b-tooltip>
                </h6>
              </div>
            </template>
            <div class="row">
              <div class="col-6">
                <div class="info-item-premium">
                  <div class="info-icon-premium bg-gradient-primary">
                    <feather-icon
                      icon="CalendarIcon"
                      size="16"
                      class="text-white"
                    />
                  </div>
                  <div class="info-content-premium">
                    <small
                      id="quickview-meta-info"
                      class="text-uppercase font-weight-bold text-muted"
                    >Arrivée au hub central</small>
                    <h6 class="mb-0 font-weight-bold text-primary">
                      {{ order.central_hub_received_at ? formatDate(order.central_hub_received_at) : '-' }}
                    </h6>
                  </div>
                </div>
              </div>
              <div class="col-6">
                <div class="info-item-premium">
                  <div class="info-icon-premium bg-gradient-secondary">
                    <feather-icon
                      icon="CalendarIcon"
                      size="16"
                      class="text-white"
                    />
                  </div>
                  <div class="info-content-premium">
                    <small class="text-uppercase font-weight-bold text-muted">Date de création</small>
                    <h6 class="mb-0 font-weight-bold text-secondary">
                      {{ order.created_at ? formatDate(order.created_at) : '-' }}
                    </h6>
                  </div>
                </div>
              </div>
            </div>
          </b-card>

          <!-- Fragile & Échange indicators -->
          <div class="row mb-3">
            <div
              v-if="order.is_fragile"
              class="col-6"
            >
              <div
                class="info-item-premium"
                style="background: #fffbe6;"
              >
                <div
                  class="info-icon-premium"
                  style="background: #ffe066;"
                >
                  <feather-icon
                    icon="AlertTriangleIcon"
                    class="text-warning"
                    size="18"
                  />
                </div>
                <div class="info-content-premium">
                  <small class="text-uppercase font-weight-bold text-warning">Colis fragile</small>
                  <h6 class="mb-0 font-weight-bold text-warning">
                    <b-badge variant="warning">
                      OUI (Colis fragile)
                    </b-badge>
                  </h6>
                </div>
              </div>
            </div>
            <div
              v-if="order.is_an_exchange"
              class="col-6"
            >
              <div
                class="info-item-premium"
                style="background: #ffeaea;"
              >
                <div
                  class="info-icon-premium"
                  style="background: #ff6b6b;"
                >
                  <feather-icon
                    icon="RefreshCwIcon"
                    class="text-danger"
                    size="18"
                  />
                </div>
                <div class="info-content-premium">
                  <small class="text-uppercase font-weight-bold text-danger">Colis d'échange</small>
                  <h6 class="mb-0 font-weight-bold text-danger">
                    <b-badge variant="danger">
                      OUI (Colis d'échange)
                    </b-badge>
                  </h6>
                </div>
              </div>
            </div>
            <div
              v-if="order.can_open_package"
              class="col-6"
            >
              <div
                class="info-item-premium"
                style="background: #f5f0eb;"
              >
                <div
                  class="info-icon-premium"
                  style="background: #C59C6C;"
                >
                  <feather-icon
                    icon="PackageIcon"
                    class="text-white"
                    size="18"
                  />
                </div>
                <div class="info-content-premium">
                  <small
                    class="text-uppercase font-weight-bold"
                    style="color: #C59C6C;"
                  >Possibilité d'ouverture</small>
                  <h6
                    class="mb-0 font-weight-bold"
                    style="color: #C59C6C;"
                  >
                    <b-badge style="background-color: #C59C6C;">
                      OUI (Ouverture autorisée)
                    </b-badge>
                  </h6>
                </div>
              </div>
            </div>
          </div>

          <!-- Tickets Related to This Order -->
          <b-card
            v-if="order.tickets && order.tickets.length"
            class="modern-card mb-3"
          >
            <template #header>
              <div class="card-header-content">
                <div class="header-icon-wrapper-custom">
                  <feather-icon
                    icon="InfoIcon"
                    class="text-white"
                    size="16"
                  />
                </div>
                <h6 class="mb-0 ml-2">
                  Tickets d'assistance liés à ce colis
                  <b-tooltip
                    target="quickview-tickets-info"
                    placement="top"
                  >
                    Liste des tickets de support associés à ce colis.
                  </b-tooltip>
                </h6>
              </div>
            </template>
            <div class="p-2">
              <ul class="list-unstyled mb-0">
                <li
                  v-for="ticket in order.tickets"
                  :key="ticket.id"
                  class="mb-2"
                >
                  <b-card class="mb-1 p-2">
                    <div class="d-flex align-items-center justify-content-between">
                      <div>
                        <b-link
                          id="quickview-tickets-info"
                          :to="{ name: 'SupportTicketDetail', params: { id: ticket.id } }"
                        >
                          <b-badge
                            variant="danger"
                            class="mr-2"
                          >
                            #{{ ticket.reference }}
                          </b-badge>
                        </b-link>
                        <b-badge
                          variant="info"
                          class="ml-2"
                        >
                          {{ ticket.status && ticket.status.display_name ? ticket.status.display_name : (ticket.status && ticket.status.name) || 'N/A' }}
                        </b-badge>
                        <span
                          v-if="ticket.category && ticket.category.display_name"
                          class="ml-2 text-muted small"
                        >({{ ticket.category.display_name }})</span>
                        <span class="ml-2 text-muted small">
                          Créé le {{ formatDate(ticket.created_at) }}
                        </span>
                        <span
                          v-if="ticket.closed_at"
                          class="ml-2 text-muted small"
                        >
                          | Clôturé le {{ formatDate(ticket.closed_at) }}
                        </span>
                      </div>
                      <b-button
                        size="sm"
                        variant="outline-primary"
                        @click="goToTicketDetail(ticket.id)"
                      >
                        Voir
                      </b-button>
                    </div>
                  </b-card>
                </li>
              </ul>
            </div>
          </b-card>

          <!-- Order Status Card -->
          <b-card class="modern-card status-card mb-3">
            <template #header>
              <div class="card-header-content">
                <feather-icon
                  icon="TruckIcon"
                  class="card-header-icon"
                  size="16"
                />
                <h6 class="mb-0">
                  État du Colis
                </h6>
              </div>
            </template>
            <div class="p-0">
              <!-- Status Row with Enhanced Design for All Status Types -->
              <div class="row mb-2">
                <div class="col-12">
                  <!-- Status En Attente -->
                  <div
                    v-if="order.status === 0"
                    class="status-card status-waiting"
                  >
                    <div class="status-icon-wrapper warning-icon">
                      <feather-icon
                        icon="ClockIcon"
                        size="18"
                        class="text-white"
                      />
                    </div>
                    <div class="status-content">
                      <h6 class="status-title mb-0">
                        {{ getStatusText(order.status) }}
                      </h6>
                      <span class="status-subtitle">En attente de traitement</span>
                      <div class="status-pulse warning-pulse" />
                    </div>
                  </div>

                  <!-- Status En Pickup -->
                  <div
                    v-else-if="order.status === 1"
                    class="status-card status-pickup"
                  >
                    <div class="status-icon-wrapper info-icon">
                      <feather-icon
                        icon="TruckIcon"
                        size="18"
                        class="text-white"
                      />
                    </div>
                    <div class="status-content">
                      <h6 class="status-title mb-0">
                        {{ getStatusText(order.status) }}
                      </h6>
                      <span class="status-subtitle">En cours</span>
                      <div class="status-pulse info-pulse" />
                    </div>
                  </div>

                  <!-- Status Au Dépôt -->
                  <div
                    v-else-if="order.status === 2"
                    class="status-card status-depot"
                  >
                    <div class="status-icon-wrapper primary-icon">
                      <feather-icon
                        icon="HomeIcon"
                        size="18"
                        class="text-white"
                      />
                    </div>
                    <div class="status-content">
                      <h6 class="status-title mb-0">
                        {{ getStatusText(order.status) }}
                      </h6>
                      <span class="status-subtitle">Colis au dépôt</span>
                      <div class="status-pulse primary-pulse" />
                    </div>
                  </div>

                  <!-- Status En Cours -->
                  <div
                    v-else-if="order.status === 3"
                    class="status-card status-transit"
                  >
                    <div class="status-icon-wrapper secondary-icon">
                      <feather-icon
                        icon="NavigationIcon"
                        size="18"
                        class="text-white"
                      />
                    </div>
                    <div class="status-content">
                      <h6 class="status-title mb-0">
                        {{ getStatusText(order.status) }}
                      </h6>
                      <span class="status-subtitle">En cours de livraison</span>
                      <div class="status-pulse secondary-pulse" />
                    </div>
                  </div>

                  <!-- Status Livré ou Livré payé -->
                  <div
                    v-else-if="order.status === 4 || order.status === 6"
                    class="status-card status-delivered"
                  >
                    <div class="status-icon-wrapper success-icon">
                      <feather-icon
                        :icon="order.status === 4 ? 'CheckCircleIcon' : 'CheckSquareIcon'"
                        size="18"
                        class="text-white"
                      />
                    </div>
                    <div class="status-content">
                      <h6 class="status-title mb-0">
                        {{ getStatusText(order.status) }}
                      </h6>
                      <span class="status-subtitle">{{ order.status === 6 ? 'Livré et payé' : 'Colis livré' }}</span>
                      <div class="status-pulse success-pulse" />
                    </div>
                  </div>

                  <!-- Status Retour au dépôt - with return number -->
                  <div
                    v-else-if="order.status === 5"
                    class="status-card status-return"
                  >
                    <div class="status-icon-wrapper danger-icon">
                      <feather-icon
                        icon="XCircleIcon"
                        size="18"
                        class="text-white"
                      />
                    </div>
                    <div class="status-content">
                      <h6 class="status-title mb-0">
                        {{ getStatusText(order.status) }}
                      </h6>
                      <div class="return-badge">
                        <feather-icon
                          icon="AlertTriangleIcon"
                          size="12"
                          class="text-white mr-1"
                        />
                        <span>{{ order.return_number || 0 }} retour{{ order.return_number > 1 ? 's' : '' }}</span>
                      </div>
                      <div class="status-pulse danger-pulse" />
                    </div>
                  </div>

                  <!-- Status Retour au fournisseur -->
                  <div
                    v-else-if="order.status === 7"
                    class="status-card status-return-vendor"
                  >
                    <div class="status-icon-wrapper danger-icon">
                      <feather-icon
                        icon="CornerUpLeftIcon"
                        size="18"
                        class="text-white"
                      />
                    </div>
                    <div class="status-content">
                      <h6 class="status-title mb-0">
                        {{ getStatusText(order.status) }}
                      </h6>
                      <span class="status-subtitle">Retourné au fournisseur</span>
                      <div class="status-pulse danger-pulse" />
                    </div>
                  </div>

                  <!-- Status En attente de collecte -->
                  <div
                    v-else-if="order.status === 15"
                    class="status-card status-collecting"
                  >
                    <div class="status-icon-wrapper warning-icon">
                      <feather-icon
                        icon="ClockIcon"
                        size="18"
                        class="text-white"
                      />
                    </div>
                    <div class="status-content">
                      <h6 class="status-title mb-0">
                        {{ getStatusText(order.status) }}
                      </h6>
                      <span class="status-subtitle">En attente de collecte</span>
                      <div class="status-pulse warning-pulse" />
                    </div>
                  </div>

                  <!-- Status Planification -->
                  <div
                    v-else-if="order.status === 18"
                    class="status-card status-planning"
                  >
                    <div class="status-icon-wrapper info-icon">
                      <feather-icon
                        icon="CalendarIcon"
                        size="18"
                        class="text-white"
                      />
                    </div>
                    <div class="status-content">
                      <h6 class="status-title mb-0">
                        {{ getStatusText(order.status) }}
                      </h6>
                      <span class="status-subtitle">Rendez Vous</span>
                      <div class="status-pulse info-pulse" />
                    </div>
                  </div>

                  <!-- Status Livré Temporaire -->
                  <div
                    v-else-if="order.status === 19"
                    class="status-card status-temp-delivered"
                  >
                    <div class="status-icon-wrapper success-icon">
                      <feather-icon
                        icon="CheckIcon"
                        size="18"
                        class="text-white"
                      />
                    </div>
                    <div class="status-content">
                      <h6 class="status-title mb-0">
                        {{ getStatusText(order.status) }}
                      </h6>
                      <span class="status-subtitle">Livré Temporaire</span>
                      <div class="status-pulse success-pulse" />
                    </div>
                  </div>

                  <!-- Status Retour Temporaire -->
                  <div
                    v-else-if="order.status === 20"
                    class="status-card status-temp-return"
                  >
                    <div class="status-icon-wrapper warning-icon">
                      <feather-icon
                        icon="ClockIcon"
                        size="18"
                        class="text-white"
                      />
                    </div>
                    <div class="status-content">
                      <h6 class="status-title mb-0">
                        {{ getStatusText(order.status) }}
                      </h6>
                      <span class="status-subtitle">Problème livraison</span>
                      <div class="status-pulse warning-pulse" />
                    </div>
                  </div>

                  <!-- Status Retour définitif -->
                  <div
                    v-else-if="order.status === 22"
                    class="status-card status-return"
                  >
                    <div class="status-icon-wrapper danger-icon">
                      <feather-icon
                        icon="XCircleIcon"
                        size="18"
                        class="text-white"
                      />
                    </div>
                    <div class="status-content">
                      <h6 class="status-title mb-0">
                        {{ getStatusText(order.status) }}
                      </h6>
                      <span class="status-subtitle">Flux de retour terminé</span>
                      <div class="status-pulse danger-pulse" />
                    </div>
                  </div>

                  <!-- Status Relance -->
                  <div
                    v-else-if="order.status === 23"
                    class="status-card status-collecting"
                  >
                    <div class="status-icon-wrapper warning-icon">
                      <feather-icon
                        icon="RotateCcwIcon"
                        size="18"
                        class="text-white"
                      />
                    </div>
                    <div class="status-content">
                      <h6 class="status-title mb-0">
                        {{ getStatusText(order.status) }}
                      </h6>
                      <span class="status-subtitle">Nouvelle tentative programmée</span>
                      <div class="status-pulse warning-pulse" />
                    </div>
                  </div>

                  <!-- Anomalie de livraison Status (24) -->
                  <div
                    v-else-if="order.status === 24"
                    class="status-card status-returned"
                  >
                    <div class="status-icon-wrapper danger-icon">
                      <feather-icon
                        icon="AlertTriangleIcon"
                        size="18"
                        class="text-white"
                      />
                    </div>
                    <div class="status-content">
                      <h6 class="status-title mb-0">
                        {{ getStatusText(order.status) }}
                      </h6>
                      <span class="status-subtitle">Anomalie détectée lors de la livraison</span>
                      <div class="status-pulse danger-pulse" />
                    </div>
                  </div>

                  <!-- Default/Unknown Status -->
                  <div
                    v-else
                    class="status-card status-unknown"
                  >
                    <div class="status-icon-wrapper default-icon">
                      <feather-icon
                        icon="HelpCircleIcon"
                        size="18"
                        class="text-white"
                      />
                    </div>
                    <div class="status-content">
                      <h6 class="status-title mb-0">
                        {{ getStatusText(order.status) }}
                      </h6>
                      <span class="status-subtitle">Statut inconnu</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Comments/Notes (if available) -->
              <div
                v-if="order.comments"
                class="mt-3 comments-section"
              >
                <div class="comments-header">
                  <feather-icon
                    icon="MessageSquareIcon"
                    class="text-primary mr-2"
                    size="14"
                  />
                  <span class="text-uppercase font-weight-bold text-muted">Commentaires</span>
                </div>
                <div class="comments-content">
                  <p class="mb-0">
                    {{ order.comments }}
                  </p>
                </div>
              </div>
            </div>
          </b-card>

          <!-- Order History Timeline Card -->
          <b-card class="modern-card history-card mt-3 mb-3">
            <template #header>
              <div class="card-header-content">
                <div class="header-icon-wrapper-history">
                  <feather-icon
                    icon="ClockIcon"
                    class="text-white"
                    size="16"
                  />
                </div>
                <h6 class="mb-0 ml-2">
                  Suivi des événements ({{ orderHistory && orderHistory.length > 0 ? orderHistory.length : 0 }})
                  <b-tooltip
                    target="quickview-timeline-info"
                    placement="top"
                  >
                    Chronologie détaillée des statuts et actions logistiques du colis.
                  </b-tooltip>
                </h6>
              </div>
            </template>
            <div class="p-0">
              <!-- Loading State -->
              <div
                v-if="isHistoryLoading"
                class="p-3 text-center"
              >
                <div class="timeline-loader mb-2" />
                <p class="text-muted small mb-0">
                  Chargement de l'historique...
                </p>
              </div>

              <!-- Error State -->
              <div
                v-else-if="historyError"
                class="p-3 text-center"
              >
                <p class="text-danger small mb-0">
                  {{ historyError }}
                </p>
                <b-button
                  variant="link"
                  size="sm"
                  class="p-0 mt-1"
                  @click="fetchOrderHistory"
                >
                  <small>Réessayer</small>
                </b-button>
              </div>

              <!-- No History State -->
              <div
                v-else-if="!orderHistory || orderHistory.length === 0"
                class="p-3 text-center"
              >
                <p class="text-muted small mb-0">
                  Aucun historique disponible
                </p>
              </div>

              <!-- History Timeline -->
              <div
                v-else
                class="history-timeline p-2"
              >
                <div
                  v-for="(event, index) in orderHistory"
                  id="quickview-timeline-info"
                  :key="event.id"
                  class="timeline-item"
                  :class="{'last-item': index === orderHistory.length - 1}"
                >
                  <div class="timeline-icon-container">
                    <div
                      class="timeline-icon"
                      :class="`bg-gradient-${getHistoryStatusVariant(event.statusCode)}`"
                    >
                      <feather-icon
                        :icon="getHistoryStatusIcon(event.statusCode)"
                        size="14"
                        class="text-white"
                      />
                    </div>
                  </div>

                  <div class="timeline-content">
                    <div class="timeline-header">
                      <h6 class="timeline-title mb-0">
                        {{ event.status }}
                      </h6>
                      <span class="timeline-date">{{ formatHistoryDate(event.date) }}</span>
                    </div>

                    <div class="timeline-body">
                      <p
                        v-if="event.description"
                        class="mb-1"
                      >
                        {{ event.description }}
                      </p>
                      <div
                        v-if="event.user"
                        class="timeline-user"
                      >
                        <feather-icon
                          icon="UserIcon"
                          size="12"
                          class="mr-1"
                        />
                        <small>{{ event.user }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-card>

          <!-- Dernier motif de retour (Latest Return Reason) -->
          <b-card
            v-if="order.last_return_reason && order.last_return_reason.return_reason"
            class="modern-card mb-3"
          >
            <template #header>
              <div class="card-header-content">
                <feather-icon
                  icon="AlertTriangleIcon"
                  class="text-warning"
                  size="16"
                />
                <h6 class="mb-0 ml-2">
                  Dernier motif de retour
                </h6>
              </div>
            </template>
            <div class="p-2">
              <b-badge
                variant="warning"
                class="mb-2"
              >
                {{ order.last_return_reason.return_reason }}
              </b-badge>
              <div class="mb-1">
                <small class="text-muted">{{ formatDate(order.last_return_reason.created_at) }}</small>
              </div>
              <ul
                class="pl-3 mb-0"
                style="font-size: 0.95em;"
              >
                <li v-if="order.last_return_reason.scheduled_date">
                  <strong>Date programmée :</strong>
                  {{ formatDate(order.last_return_reason.scheduled_date) }}
                </li>
                <li v-if="order.last_return_reason.additional_note">
                  <strong class="text-info">Remarque :</strong>
                  <span class="remarque-text">{{ order.last_return_reason.additional_note }}</span>
                </li>
                <li v-if="order.last_return_reason.quick_options && order.last_return_reason.quick_options.length">
                  <strong>Note :</strong>
                  <span>
                    <span
                      v-for="(opt, idx) in order.last_return_reason.quick_options"
                      :key="idx"
                    >
                      <b-badge
                        variant="secondary"
                        class="mr-1"
                      >{{ opt }}</b-badge>
                    </span>
                  </span>
                </li>
              </ul>
            </div>
          </b-card>

          <!-- Historique des Retours (Return Reason History) -->
          <b-card
            v-if="order.return_reason_histories && order.return_reason_histories.length > 0"
            class="modern-card mb-3"
          >
            <template #header>
              <div class="card-header-content">
                <feather-icon
                  icon="AlertTriangleIcon"
                  class="text-warning"
                  size="16"
                />
                <h6 class="mb-0 ml-2">
                  Historique des retours ({{ order.return_number }})
                  <b-tooltip
                    target="quickview-returns-info"
                    placement="top"
                  >
                    Liste chronologique des motifs de retour et actions associées.
                  </b-tooltip>
                </h6>
              </div>
            </template>
            <div class="returns-list">
              <div
                v-for="(returnItem, index) in order.return_reason_histories"
                id="quickview-returns-info"
                :key="index"
                class="return-item mb-2"
              >
                <div class="return-content">
                  <b-badge
                    variant="warning"
                    class="reason-badge"
                  >
                    {{ returnItem.return_reason }}
                  </b-badge>
                  <small class="return-date ml-2">{{ formatDate(returnItem.created_at) }}</small>
                </div>
                <ul
                  v-if="returnItem.scheduled_date || returnItem.additional_note || (returnItem.quick_options && returnItem.quick_options.length)"
                  class="pl-3 mb-0"
                  style="font-size: 0.95em;"
                >
                  <li v-if="returnItem.scheduled_date">
                    <strong>Date programmée :</strong>
                    {{ formatDate(returnItem.scheduled_date) }}
                  </li>
                  <li v-if="returnItem.additional_note">
                    <strong class="text-info">Remarque :</strong>
                    <span class="remarque-text">{{ returnItem.additional_note }}</span>
                  </li>
                  <li v-if="returnItem.quick_options && returnItem.quick_options.length">
                    <strong>Note :</strong>
                    <span>
                      <span
                        v-for="(opt, idx) in returnItem.quick_options"
                        :key="idx"
                      >
                        <b-badge
                          variant="secondary"
                          class="mr-1"
                        >{{ opt }}</b-badge>
                      </span>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </b-card>

          <!-- Produits (Products List) -->
          <b-card
            v-if="order.products && order.products.length"
            class="modern-card mb-3"
          >
            <template #header>
              <div class="card-header-content">
                <feather-icon
                  icon="ShoppingBagIcon"
                  class="text-primary"
                  size="16"
                />
                <h6 class="mb-0 ml-2">
                  Produits ({{ order.products.length }})
                </h6>
              </div>
            </template>
            <div class="products-list">
              <div
                v-for="item in order.products"
                :key="item.product"
                class="product-item d-flex align-items-center mb-2"
              >
                <div class="product-name-premium flex-grow-1">
                  <h6 class="mb-0">
                    {{ item.product }}
                  </h6>
                </div>
                <div class="quantity-badge ml-2">
                  <span class="badge-value">x{{ item.quantity }}</span>
                </div>
              </div>
            </div>
          </b-card>

          <!-- Tarifs (Pricing Breakdown) -->
          <b-card class="modern-card mb-3">
            <template #header>
              <div class="card-header-content">
                <feather-icon
                  icon="DollarSignIcon"
                  class="text-success"
                  size="16"
                />
                <h6 class="mb-0 ml-2">
                  Détail tarifaire
                  <b-tooltip
                    target="quickview-pricing-info"
                    placement="top"
                  >
                    Décomposition du prix du colis (HT, TVA, TTC).
                  </b-tooltip>
                </h6>
              </div>
            </template>
            <div class="pricing-breakdown">
              <div class="pricing-row">
                <span
                  id="quickview-pricing-info"
                  class="pricing-label"
                >Prix HT</span>
                <span class="pricing-value">{{ order.price_ht }} DT</span>
              </div>
              <div class="pricing-row">
                <span class="pricing-label">TVA</span>
                <span class="pricing-value">{{ order.tax }} DT</span>
              </div>
              <div class="pricing-divider" />
              <div class="pricing-row total">
                <span class="pricing-label">Prix TTC</span>
                <span class="pricing-value total-amount">{{ order.price_ttc }} DT</span>
              </div>
            </div>
          </b-card>

          <!-- Fournisseur (Supplier Info) -->
          <b-card
            v-if="order.supplier"
            class="modern-card mb-3"
          >
            <template #header>
              <div class="card-header-content">
                <feather-icon
                  icon="BuildingIcon"
                  class="text-secondary"
                  size="16"
                />
                <h6 class="mb-0 ml-2">
                  Fournisseur
                </h6>
              </div>
            </template>
            <div class="supplier-info d-flex align-items-center">
              <b-avatar
                :text="order.supplier.username.charAt(0).toUpperCase()"
                variant="primary"
                size="40"
                class="mr-3"
              />
              <div class="supplier-details">
                <h6 class="supplier-name mb-1">
                  {{ order.supplier.username }}
                </h6>
                <div class="supplier-field mb-1">
                  <feather-icon
                    icon="MailIcon"
                    size="14"
                    class="field-icon mr-1"
                  />
                  <span class="field-value">{{ order.supplier.email }}</span>
                </div>
                <div class="supplier-field mb-1">
                  <feather-icon
                    icon="PhoneIcon"
                    size="14"
                    class="field-icon mr-1"
                  />
                  <span class="field-value">{{ order.supplier.phone }}<span v-if="order.supplier.phone2"> / {{ order.supplier.phone2 }}</span></span>
                </div>
                <div class="supplier-field mb-1">
                  <feather-icon
                    icon="MapPinIcon"
                    size="14"
                    class="field-icon mr-1"
                  />
                  <span class="field-value">{{ order.supplier.address }}</span>
                </div>
                <div
                  v-if="order.supplier.tax_identification_number"
                  class="supplier-field mb-1"
                >
                  <feather-icon
                    icon="FileTextIcon"
                    size="14"
                    class="field-icon mr-1"
                  />
                  <span class="field-value">{{ order.supplier.tax_identification_number }}</span>
                </div>
              </div>
            </div>
          </b-card>
        </div>

        <!-- No Data State -->
        <div
          v-else
          class="text-center p-5 no-data-container"
        >
          <div class="no-data-icon-wrapper mb-3">
            <feather-icon
              icon="PackageIcon"
              size="36"
            />
          </div>
          <p class="text-muted">
            Aucun détail disponible
          </p>
        </div>
      </div>

      <!-- Modern Footer -->
      <div class="sidebar-footer">
        <b-button
          variant="outline-secondary"
          class="btn-close-drawer"
          @click="closeDrawer"
        >
          <feather-icon
            icon="XIcon"
            class="mr-1"
            size="14"
          />
          Fermer
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BLink,
  BAvatar,
} from 'bootstrap-vue'
import axios from 'axios'

export default {
  name: 'OrderQuickView',
  components: {
    BCard,
    BButton,
    BLink,
    BAvatar,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    orderId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      isVisible: false,
      order: null,
      isLoading: false,
      error: null,
      orderHistory: [],
      isHistoryLoading: false,
      historyError: null,
    }
  },
  computed: {
    tickets() {
      return this.order && this.order.tickets ? this.order.tickets : []
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(newVal) {
        this.isVisible = newVal
        if (newVal && this.orderId) {
          this.fetchOrderDetails()
        }
      },
    },
    orderId: {
      handler(newVal) {
        if (newVal && this.visible) {
          this.fetchOrderDetails()
        }
      },
    },
  },
  methods: {
    async fetchOrderDetails() {
      if (!this.orderId) return

      this.isLoading = true
      this.error = null
      this.order = null

      try {
        // Use detail endpoint which includes histories
        const { data } = await axios.get(`/api/orders/detail/${this.orderId}/`)
        this.order = data

        // After successfully loading order details, fetch/process history
        this.fetchOrderHistory()
      } catch (error) {
        this.error = 'Erreur lors du chargement des détails du colis'
      } finally {
        this.isLoading = false
      }
    },

    async fetchOrderHistory() {
      if (!this.orderId || !this.order) return

      this.isHistoryLoading = true
      this.historyError = null

      try {
        // Only use histories from the order details; do not make a separate API call
        if (this.order.histories && this.order.histories.length > 0) {
          this.orderHistory = this.order.histories.map(item => ({
            id: item.id,
            date: new Date(item.created_at),
            status: item.message,
            statusCode: this.getStatusCodeFromMessage(item.message),
            description: item.description || '',
            user: item.done_by || '',
          })).sort((a, b) => b.date - a.date) // Most recent first
        } else {
          this.orderHistory = []
        }
      } catch (error) {
        this.historyError = 'Erreur lors du chargement de l\'historique'
      } finally {
        this.isHistoryLoading = false
      }
    },

    getStatusCodeFromMessage(message) {
      const messageToStatus = {
        'En attente': 0,
        'En pickup': 1,
        'Au dépôt': 2,
        'En cours': 3,
        Livré: 4,
        'Retour au dépôt': 5,
        'Livré payé': 6,
        'Retour au fournisseur': 7,
        'En attente de collecte': 15,
      }

      // Use Object.keys and forEach instead of for...of
      return Object.keys(messageToStatus).reduce((result, key) => {
        if (message.includes(key)) {
          return messageToStatus[key]
        }
        return result
      }, 0) // Default status
    },
    closeDrawer() {
      this.isVisible = false
      this.$emit('close')
    },
    getStatusVariant(status) {
      const variants = {
        0: 'warning',
        1: 'info',
        2: 'primary',
        3: 'secondary',
        4: 'success',
        5: 'danger',
        6: 'success',
        7: 'danger',
        15: 'warning',
        22: 'danger',
        23: 'warning',
      }
      return variants[status] || 'secondary'
    },
    getStatusText(status) {
      const texts = {
        0: 'En attente',
        1: 'En pickup',
        2: 'Au dépôt',
        3: 'En cours',
        4: 'Livré',
        5: 'Retour au dépôt',
        6: 'Livré payé',
        7: 'Retour expéditeur',
        8: 'Transfert',
        9: 'Retour payé',
        10: 'Retour inter-agence',
        11: 'Retour inter-agence reçu',
        12: 'Colis annulé par admin',
        13: 'Colis perdu',
        14: 'À vérifier',
        15: 'En attente de collecte',
        16: 'À rembourser',
        17: 'Remboursé',
        18: 'Rendez Vous',
        22: 'Retour définitif',
        23: 'Relance',
        24: 'Anomalie de livraison',
      }
      return texts[status] || 'Inconnu'
    },
    getStatusIcon(status) {
      const icons = {
        0: 'ClockIcon',
        1: 'TruckIcon',
        2: 'HomeIcon',
        3: 'NavigationIcon',
        4: 'CheckCircleIcon',
        5: 'XCircleIcon',
        6: 'CheckSquareIcon',
        7: 'CornerUpLeftIcon',
        8: 'ArrowRightIcon',
        9: 'CornerUpLeftIcon',
        10: 'ArrowLeftIcon',
        11: 'ArrowDownIcon',
        12: 'XIcon',
        13: 'AlertTriangleIcon',
        14: 'AlertCircleIcon',
        15: 'ClockIcon',
        16: 'DollarSignIcon',
        17: 'CheckIcon',
        18: 'CalendarIcon',
        22: 'XCircleIcon',
        23: 'RotateCcwIcon',
      }
      return icons[status] || 'HelpCircleIcon'
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatHistoryDate(date) {
      if (!date) return '-'

      // Convert string to date if needed
      const dateObj = typeof date === 'string' ? new Date(date) : date
      // Check if date is valid using !Number.isFinite instead of isNaN
      if (!Number.isFinite(dateObj.getTime())) return '-' // Invalid date

      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      const isToday = dateObj.toDateString() === today.toDateString()
      const isYesterday = dateObj.toDateString() === yesterday.toDateString()

      if (isToday) {
        return `Aujourd'hui à ${dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
      }

      if (isYesterday) {
        return `Hier à ${dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
      }

      return dateObj.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    getPaymentMethod(method) {
      const methods = {
        0: 'Espèces',
        1: 'Chèque',
        2: 'Traite', // Added new payment method
        cash: 'Espèces',
        card: 'Carte',
        bank_transfer: 'Virement',
        check: 'Chèque',
        cod: 'Paiement à la livraison',
        online: 'Paiement en ligne',
      }
      return methods[method] || 'Non spécifié'
    },
    getHistoryStatusIcon(statusCode) {
      // Reuse the same icons as the status cards for consistency
      return this.getStatusIcon(statusCode)
    },
    getHistoryStatusVariant(statusCode) {
      // Reuse the same color variants as the status cards for consistency
      return this.getStatusVariant(statusCode)
    },
    goToTicketDetail(ticketId) {
      this.$router.push({ name: 'SupportTicketDetail', params: { id: ticketId } })
    },
  },
}
</script>

<style scoped>
.sidebar-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
  z-index: 1040;
}

.sidebar-drawer {
  position: fixed;
  top: 0;
  right: -600px;
  width: 600px;
  height: 100%;
  background: #f8f9fa;
  box-shadow: -5px 0 25px rgba(0, 0, 0, 0.15);
  transition: right 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  z-index: 1050;
  display: flex;
  flex-direction: column;
  border-radius: 16px 0 0 16px;
}

.sidebar-drawer.sidebar-open {
  right: 0;
}

.sidebar-header {
  padding: 1.25rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background: white;
  border-radius: 16px 0 0 0;
}

.header-icon-wrapper {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: linear-gradient(45deg, #dc2626, #ef4444);
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem;
  background: #f8f9fa;
}

.sidebar-footer {
  padding: 1.25rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  background: white;
  border-radius: 0 0 0 16px;
  display: flex;
  justify-content: flex-end;
}

.btn-close-drawer {
  padding: 0.5rem 1.5rem;
  border-radius: 8px;
  box-shadow: none;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-close-drawer:hover {
  background: #e2e8f0;
  transform: translateY(-1px);
}

/* Modern Cards Styling */
.modern-card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
  overflow: hidden;
  transition: all 0.2s;
}

.modern-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
}

.modern-card ::v-deep .card-header {
  background: white;
  padding: 1rem;
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

.card-header-content {
  display: flex;
  align-items: center;
}

.card-header-icon {
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #dc2626;
}

/* Enhanced Info Items for État du Colis */
.info-item-enhanced {
  display: flex;
  align-items: center;
  padding: 8px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  transition: all 0.2s;
  height: 100%;
}

.info-item-enhanced:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.info-icon-enhanced {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
}

.info-content-enhanced {
  flex: 1;
}

.info-content-enhanced small {
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 2px;
}

.info-content-enhanced h6 {
  font-size: 1.05rem;
  line-height: 1.2;
}

.return-info-enhanced {
  display: flex;
  align-items: center;
  padding: 12px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  transition: all 0.2s;
}

.return-info-enhanced:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

/* Combined Status and Return Styling */
.combined-return-status {
  background: linear-gradient(to right, rgba(255, 243, 242, 0.7), rgba(255, 243, 242, 0.9));
  border: 1px solid rgba(220, 53, 69, 0.2);
  padding: 12px;
  transition: all 0.25s;
}

.combined-return-status:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(220, 53, 69, 0.15);
}

.combined-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.status-section {
  flex: 2;
  padding-right: 16px;
  border-right: 1px solid rgba(220, 53, 69, 0.2);
}

.return-section {
  flex: 1;
  padding-left: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Make the combined card more noticeable */
.combined-return-status .info-icon-enhanced {
  background: rgba(220, 53, 69, 0.2);
  width: 42px;
  height: 42px;
  animation: pulse-danger 2s infinite;
}

@keyframes pulse-danger {
  0% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
  }
}

/* Enhanced Premium Card Design */
.enhanced-card {
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  transition: all 0.3s ease;
  border-top: 3px solid #dc2626;
}

.enhanced-card:hover {
  box-shadow: 0 8px 30px rgba(0,0,0,0.1);
  transform: translateY(-3px);
}

.enhanced-card ::v-deep .card-header {
  background: linear-gradient(to right, rgba(220, 38, 38, 0.03), rgba(220, 38, 38, 0.08));
  border-bottom: 1px solid rgba(220, 38, 38, 0.1);
  padding: 1.25rem;
}

.header-icon-wrapper-custom {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: linear-gradient(45deg, #dc2626, #ef4444);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 10px rgba(220, 38, 38, 0.3);
}

/* Premium Info Items */
.info-item-premium {
  display: flex;
  align-items: center;
  padding: 10px;
  background: white;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.04);
  transition: all 0.25s;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.info-item-premium::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(to right, transparent, rgba(79, 70, 229, 0.2), transparent);
  opacity: 0;
  transition: opacity 0.25s;
}

.info-item-premium:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.08);
}

.info-item-premium:hover::after {
  opacity: 1;
}

.info-icon-premium {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
  box-shadow: 0 3px 6px rgba(0,0,0,0.1);
}

.info-content-premium {
  flex: 1;
}

.info-content-premium small {
  font-size: 0.65rem;
  letter-spacing: 0.6px;
  display: block;
  margin-bottom: 2px;
  font-weight: 600;
}

.info-content-premium h6 {
  font-size: 1.05rem;
  line-height: 1.3;
  letter-spacing: 0.2px;
}

/* Product and Quantity Card - Premium Version */
.product-quantity-premium {
  position: relative;
  padding-right: 60px;
}

.product-content-premium {
  width: 100%;
}

.product-name-premium {
  flex: 1;
}

.quantity-badge {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(45deg, #10b981, #34d399);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  box-shadow: 0 3px 8px rgba(16, 185, 129, 0.3);
}

.badge-value {
  font-size: 14px;
  font-weight: 700;
}

/* Gradient Backgrounds */
.bg-gradient-indigo {
  background: linear-gradient(45deg, #4f46e5, #6366f1);
}

.bg-gradient-purple {
  background: linear-gradient(45deg, #8b5cf6, #a78bfa);
}

.bg-gradient-info {
  background: linear-gradient(45deg, #0ea5e9, #38bdf8);
}

.bg-gradient-success {
  background: linear-gradient(45deg, #10b981, #34d399);
}

.bg-gradient-orange {
  background: linear-gradient(45deg, #f97316, #fb923c);
}

.bg-gradient-primary {
  background: linear-gradient(45deg, #2563eb, #3b82f6);
}

/* Enhanced Status Cards */
.status-card {
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 14px;
  background-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.status-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.status-icon-wrapper {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  flex-shrink: 0;
}

.status-content {
  flex: 1;
  position: relative;
}

.status-title {
  font-weight: 700;
  font-size: 1.05rem;
  margin-bottom: 2px;
  letter-spacing: 0.3px;
}

.status-subtitle {
  font-size: 0.75rem;
  opacity: 0.7;
  display: block;
  margin-bottom: 2px;
}

.status-pulse {
  position: absolute;
  bottom: -8px;
  left: 0;
  height: 3px;
  width: 60px;
  border-radius: 3px;
}

.return-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 12px;
  background-color: rgba(220, 53, 69, 0.9);
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  margin-top: 4px;
}

/* Status-specific styles */
.status-waiting {
  background-color: rgba(255, 248, 230, 0.6);
  border-left: 3px solid #ffc107;
}

.status-pickup {
  background-color: rgba(235, 250, 255, 0.6);
  border-left: 3px solid #0dcaf0;
}

.status-depot {
  background-color: rgba(236, 242, 255, 0.6);
  border-left: 3px solid #0d6efd;
}

.status-transit {
  background-color: rgba(241, 243, 245, 0.6);
  border-left: 3px solid #6c757d;
}

.status-delivered {
  background-color: rgba(236, 253, 245, 0.6);
  border-left: 3px solid #198754;
}

.status-return, .status-return-vendor {
  background-color: rgba(255, 243, 242, 0.6);
  border-left: 3px solid #dc3545;
}

.status-collecting {
  background-color: rgba(255, 248, 230, 0.6);
  border-left: 3px solid #fd7e14;
}

.status-planning {
  background-color: rgba(99, 102, 241, 0.1);
  border-left: 3px solid #6366f1;
}

.status-unknown {
  background-color: rgba(241, 243, 245, 0.6);
  border-left: 3px solid #adb5bd;
}

/* Icon background colors */
.warning-icon {
  background: linear-gradient(45deg, #ffc107, #ffca2c);
  box-shadow: 0 4px 10px rgba(255, 193, 7, 0.3);
}

.info-icon {
  background: linear-gradient(45deg, #0dcaf0, #39c0e7);
  box-shadow: 0 4px 10px rgba(13, 202, 240, 0.3);
}

.primary-icon {
  background: linear-gradient(45deg, #0d6efd, #3d8bfd);
  box-shadow: 0 4px 10px rgba(13, 110, 253, 0.3);
}

.secondary-icon {
  background: linear-gradient(45deg, #6c757d, #858e96);
  box-shadow: 0 4px 10px rgba(108, 117, 125, 0.3);
}

.success-icon {
  background: linear-gradient(45deg, #198754, #28a745);
  box-shadow: 0 4px 10px rgba(25, 135, 84, 0.3);
}

.danger-icon {
  background: linear-gradient(45deg, #dc3545, #e35d6a);
  box-shadow: 0 4px 10px rgba(220, 53, 69, 0.3);
}

/* Pulsing effects */
.warning-pulse {
  background: linear-gradient(to right, #ffc107, #ffca2c);
  animation: pulse-status 2s infinite;
}

.info-pulse {
  background: linear-gradient(to right, #0dcaf0, #39c0e7);
  animation: pulse-status 2s infinite 0.3s;
}

.primary-pulse {
  background: linear-gradient(to right, #0d6efd, #3d8bfd);
  animation: pulse-status 2s infinite 0.5s;
}

.secondary-pulse {
  background: linear-gradient(to right, #6c757d, #858e96);
  animation: pulse-status 2s infinite 0.2s;
}

.success-pulse {
  background: linear-gradient(to right, #198754, #28a745);
  animation: pulse-status 2s infinite;
}

.danger-pulse {
  background: linear-gradient(to right, #dc3545, #e35d6a);
  animation: pulse-status 2s infinite;
}

@keyframes pulse-status {
  0% {
    opacity: 0.6;
    width: 40px;
  }
  50% {
    opacity: 1;
    width: 80px;
  }
  100% {
    opacity: 0.6;
    width: 40px;
  }
}

/* History Card Styling */
.history-card {
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  transition: all 0.3s ease;
  border-top: 3px solid #dc2626;
}

.history-card:hover {
  box-shadow: 0 8px 30px rgba(0,0,0,0.1);
  transform: translateY(-2px);
}

.history-card ::v-deep .card-header {
  background: linear-gradient(to right, rgba(220, 38, 38, 0.03), rgba(220, 38, 38, 0.08));
  border-bottom: 1px solid rgba(220, 38, 38, 0.1);
  padding: 1.25rem;
}

.header-icon-wrapper-history {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: linear-gradient(45deg, #dc2626, #ef4444);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 10px rgba(220, 38, 38, 0.3);
}

/* Timeline Styling */
.history-timeline {
  position: relative;
  padding: 16px 8px;
}

.timeline-item {
  display: flex;
  position: relative;
  margin-bottom: 24px;
}

.timeline-item::before {
  content: '';
  position: absolute;
  top: 24px;
  bottom: -24px;
  left: 15px;
  width: 1px;
  background: linear-gradient(to bottom, rgba(220, 38, 38, 0.5), rgba(220, 38, 38, 0.1));
  z-index: 0;
}

.timeline-item.last-item::before {
  display: none;
}

.timeline-icon-container {
  position: relative;
  z-index: 2;
  margin-right: 16px;
  flex-shrink: 0;
}

.timeline-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.timeline-content {
  flex: 1;
  background: white;
  border-radius: 10px;
  padding: 12px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  position: relative;
  z-index: 1;
  transition: all 0.2s;
}

.timeline-content:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.timeline-title {
  font-weight: 700;
  color: #4b5563;
  margin-right: 8px;
}

.timeline-date {
  font-size: 0.75rem;
  color: #6b7280;
  white-space: nowrap;
}

.timeline-body {
  font-size: 0.875rem;
  color: #374151;
}

.timeline-body p {
  margin-bottom: 6px;
  line-height: 1.4;
}

.timeline-user {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: #6b7280;
}

/* Timeline Loading Animation */
.timeline-loader {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 3px solid rgba(99, 102, 241, 0.1);
  border-top-color: #6366f1;
  margin: 0 auto;
  animation: timeline-spinner 0.8s linear infinite;
}

@keyframes timeline-spinner {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 767.98px) {
  .sidebar-backdrop {
    backdrop-filter: none;
  }

  .sidebar-drawer {
    right: -100vw;
    width: 100vw;
    max-width: 100vw;
    border-radius: 0;
    box-shadow: none;
  }

  .sidebar-drawer.sidebar-open {
    right: 0;
  }

  .sidebar-header {
    padding: 0.85rem 1rem;
    border-radius: 0;
  }

  .sidebar-header h5 {
    min-width: 0;
    max-width: calc(100% - 48px);
    font-size: 1rem;
    line-height: 1.25;
  }

  .sidebar-header h5 span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .header-icon-wrapper {
    width: 32px;
    height: 32px;
    margin-right: 8px;
    border-radius: 10px;
    flex-shrink: 0;
  }

  .sidebar-content {
    padding: 0.75rem;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }

  .sidebar-footer {
    padding: 0.75rem;
    border-radius: 0;
  }

  .btn-close-drawer {
    width: 100%;
    min-height: 42px;
  }

  .modern-card,
  .enhanced-card,
  .history-card {
    border-radius: 10px;
    margin-bottom: 0.75rem !important;
    transform: none !important;
  }

  .modern-card ::v-deep .card-header,
  .enhanced-card ::v-deep .card-header,
  .history-card ::v-deep .card-header {
    padding: 0.75rem;
  }

  .card-header-content {
    align-items: flex-start;
    min-width: 0;
  }

  .card-header-content h6 {
    min-width: 0;
    font-size: 0.95rem;
    line-height: 1.25;
    overflow-wrap: anywhere;
  }

  .sidebar-content .row {
    margin-right: 0;
    margin-left: 0;
  }

  .sidebar-content .row > [class*="col-"] {
    flex: 0 0 100%;
    max-width: 100%;
    padding-right: 0;
    padding-left: 0;
    margin-bottom: 0.5rem;
  }

  .info-item-premium,
  .info-item-enhanced,
  .return-info-enhanced,
  .status-card {
    min-width: 0;
    width: 100%;
    padding: 0.7rem;
    align-items: flex-start;
  }

  .info-icon-premium,
  .info-icon-enhanced,
  .status-icon-wrapper,
  .header-icon-wrapper-custom,
  .header-icon-wrapper-history {
    width: 34px;
    height: 34px;
    margin-right: 0.65rem;
    border-radius: 10px;
    flex-shrink: 0;
  }

  .info-content-premium,
  .info-content-enhanced,
  .status-content,
  .timeline-content,
  .product-content-premium {
    min-width: 0;
  }

  .info-content-premium small,
  .info-content-enhanced small {
    font-size: 0.58rem;
    line-height: 1.15;
    overflow-wrap: anywhere;
  }

  .info-content-premium h6,
  .info-content-enhanced h6,
  .status-title,
  .timeline-title,
  .timeline-body,
  .timeline-user,
  .return-badge {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .info-content-premium h6,
  .info-content-enhanced h6 {
    font-size: 0.95rem;
    line-height: 1.25;
  }

  .text-truncate {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
  }

  .product-quantity-premium {
    padding-right: 0;
  }

  .quantity-badge {
    position: static;
    transform: none;
    margin-left: 0.5rem;
    flex-shrink: 0;
  }

  .combined-content {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .status-section {
    padding-right: 0;
    border-right: 0;
    border-bottom: 1px solid rgba(220, 53, 69, 0.2);
    padding-bottom: 0.75rem;
  }

  .return-section {
    padding-left: 0;
    align-items: flex-start;
  }

  .history-timeline {
    padding: 0.5rem 0;
  }

  .timeline-item {
    margin-bottom: 1rem;
  }

  .timeline-icon-container {
    margin-right: 0.65rem;
  }

  .timeline-content {
    padding: 0.7rem;
  }

  .timeline-header {
    display: block;
  }

  .timeline-date {
    display: block;
    margin-top: 0.15rem;
    white-space: normal;
  }
}

@media (max-width: 380px) {
  .sidebar-content {
    padding: 0.5rem;
  }

  .info-item-premium,
  .info-item-enhanced,
  .return-info-enhanced,
  .status-card {
    padding: 0.6rem;
  }

  .info-icon-premium,
  .info-icon-enhanced,
  .status-icon-wrapper {
    width: 30px;
    height: 30px;
    margin-right: 0.5rem;
  }

  .info-content-premium h6,
  .info-content-enhanced h6 {
    font-size: 0.88rem;
  }
}
</style>
